// src/hooks/useSendMessage.js
import { useState, useRef } from 'react'
import { buildIframeDoc, sanitizeHead } from '../utils/htmlSanitizer.js'

import usePreferences from '../contexts/PreferencesContext.js'
import { sendToOpenAI, sendToLocal } from '../services/chatApi.js'
import { buildPrompt }               from '../Prompt.js'
import { extractCodeBlocks }         from '@openjam/shared/utils/responseParser.js';
import { fileToBase64 }              from '../utils/file.js'
import { asString }                  from '../utils/strings.js'
import captureThumbnail              from '../utils/captureThumbnail.js';


export default function useSendMessage({
  chatIdRef,
  persistMessage,
  setMessages,
  appendVersion,
  lastHtml,
  lastCss,
  lastJs,
  historyIndex,
  timestamps,
  iframeRef,
  setInput,
  setUploadedImage,
  input,
  uploadedImage,
  setOptionState,
  setHoveredId,
  jumpToOption,
  setCanvasContent,
  setLastHtml,
  setLastCss,
  setLastJs
}) {
  const { modality, model: selectedModel } = usePreferences()
  const [loading, setLoading] = useState(false)

  const jumpRef = useRef(jumpToOption);
  jumpRef.current = jumpToOption;

  const sendMessage = async (
    overrideText,
    overrideFile,
    { showUser = true, skipUserPersist = false } = {}
  ) => {
    const raw = overrideText ?? input;
    const userText = asString(raw).trim();
    if (!userText) return;

    const file = overrideFile !== undefined ? overrideFile : uploadedImage;

    // 1) Enqueue user bubble
    const nowUser = new Date().toISOString();
    setMessages((m) => [
      ...m,
      { key: `user-${nowUser}`, sender: 'user', text: userText, timestamp: nowUser }
    ]);

    // 2) Placeholder AI bubble
    const aiPlaceholderKey = `ai-${Date.now()}`;
    setMessages((m) => [
      ...m,
      {
        key:       aiPlaceholderKey,
        sender:    'ai',
        text:      'Generating…',
        timestamp: new Date().toISOString()
      }
    ]);

    setInput('');
    setUploadedImage(null);
    setLoading(true);

    // 3) Persist user→DB unless skipUserPersist
    let userMessageId = null;
    if (!skipUserPersist) {
      userMessageId = await persistMessage('user', userText);
    }

    // 4) Build prompt (including any image)
    let img64 = null;
    if (file) {
      try { img64 = await fileToBase64(file); } catch {}
    }
    const prompt = buildPrompt({
      userText,
      hasImage: Boolean(img64),
      lastHtml, lastCss, lastJs,
      historyIndex, timestamps
    });

    try {
      // ─── If this is the FIRST prompt (skipUserPersist===true), do n=3 via /api/chat ───
      if (skipUserPersist) {
        // 4a) Call /api/chat with n=3 and chatId so the server persists option_set+options
        const resp = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: selectedModel,
            prompt,
            n: 3,
            chatId: chatIdRef.current
          })
        });
        if (!resp.ok) throw new Error('OpenAI/DB error');
        const { optionSetId, options } = await resp.json();

        // ─── Enrich each raw option by extracting HTML/CSS/JS from raw_text
        const enriched = options.map(opt => {
          const { htmlCode, cssCode, jsCode } = extractCodeBlocks(opt.raw_text);
          return {
            ...opt,
            html: htmlCode || "",
            css:  cssCode  || "",
            js:   jsCode   || ""
          };
        });

        setOptionState({
          optionSetId,
          status: 'pending',
          options: enriched,
          hoveredId: enriched[0]?.id ?? null,
          acceptedId: null
        });
        // Immediately trigger a “hover” of Option A so that it renders
        if (enriched[0]?.id != null) {
          setHoveredId(enriched[0].id);
          // immediately set as default the first option
          // build a full HTML doc right away
          const doc = buildIframeDoc({
            cleanHead: sanitizeHead(''),
            cssCode  : enriched[0].css,
            bodyInner: enriched[0].html,
            jsCode   : enriched[0].js,
          });
          setCanvasContent(doc);
          setLastHtml(enriched[0].html);
          setLastCss (enriched[0].css);
          setLastJs  (enriched[0].js);
          setHoveredId(enriched[0].id);   // keeps the button highlighted later
        }

        // 4b) Replace “Generating…” placeholder with a single AI bubble carrying metadata
        const nowAi = new Date().toISOString();
        setMessages((m) =>
          m
            .filter((msg) => msg.key !== aiPlaceholderKey)
            .concat({
              key: `ai-${nowAi}`,
              sender: 'ai',
              text: null,                // we’ll render buttons instead of plain text
              timestamp: nowAi,
              metadata: {
                isOptionBatch: true,
                optionSetId,
                options: enriched
              }
            })
        );

        setLoading(false);
        return;
      }

      // ─── Otherwise: normal n=1 flow ─────────────────────────────────────────────
      let aiText = '';
      if (modality === 'Cloud') {
        // Since /api/chat now always returns either { text } or { optionSetId, options },
        // we can ask for n=1 here:
        const resp = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: selectedModel,
            prompt,
            n: 1
          })
        });
        if (!resp.ok) throw new Error('OpenAI proxy error');
        const json = await resp.json();
        aiText = json.text ?? ''; 
      } else {
        // Local‐LLM path unchanged
        const local = await sendToLocal({ model: selectedModel, prompt, images: img64 ? [img64] : [] });
        aiText = asString(local);
      }

      // 5) Swap in real AI text
      const nowAi = new Date().toISOString();
      setMessages((m) =>
        m
          .filter((msg) => msg.key !== aiPlaceholderKey)
          .concat({ key: `ai-${nowAi}`, sender: 'ai', text: aiText, timestamp: nowAi })
      );

      // 6) Persist AI→DB
      const aiMessageId = await persistMessage('ai', aiText);

      // 7) Extract code blocks, create a new Version, and capture thumbnail
      let { htmlCode, cssCode, jsCode } = extractCodeBlocks(aiText);
      if (htmlCode) htmlCode = htmlCode.replace(/^<!--.*?-->\r?\n?/, '');
      if (cssCode) cssCode = cssCode.replace(/^\/\*.*?\*\/\r?\n?/, '');
      if (jsCode) jsCode = jsCode.replace(/^\/\/.*?\r?\n?/, '');

      if (htmlCode || cssCode || jsCode) {
        await appendVersion({
          htmlCode,
          cssCode,
          jsCode,
          userMessageId,
          aiMessageId
        });

        requestAnimationFrame(async () => {
          try {
            const thumb = await captureThumbnail(iframeRef.current);
            if (thumb && chatIdRef.current) {
              await fetch(`/api/prototypes/${chatIdRef.current}/thumbnail`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ thumbnail: thumb })
              });
            }
          } catch {}
        });
      }

      setLoading(false);
    } catch (err) {
      console.error(err);
      // Replace placeholder with error bubble
      setMessages((m) =>
        m
          .filter((msg) => msg.key !== aiPlaceholderKey)
          .concat({
            key: `ai-error-${Date.now()}`,
            sender: 'ai',
            text: 'AI: (Error)',
            timestamp: new Date().toISOString()
          })
      );
      setLoading(false);
    }
  };

  return { sendMessage, loading }
}
